/** @jsxImportSource @emotion/react */

import { useSession } from '@quickbit/backoffice-core-frontend';
import {
  Input,
  useTheme,
  createStyles,
  Typography,
  Button,
  Card,
} from '@quickbit/qb-design-system';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Recipient } from 'api/walletRecipientApi.types';
import useWithClickOutside from './hooks/useWithClickOutside';

interface Props {
  address: string;
  label: string;
  recipients?: Recipient[];
  onChangeRecipient: (recipient: { label: string; address: string }) => void;
}

export const Destination = ({
  address,
  label,
  onChangeRecipient,
  recipients,
}: Props) => {
  const session = useSession();
  const canAddRecipients =
    session.authenticated &&
    session.permissions.includes('edit_corporate_wallet');
  const { spacing, palette } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const styles = useStyles();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLInputElement>(null);

  useWithClickOutside(wrapperRef, () => {
    setIsOpen(false);
  });

  const onSetContact = (label: string, address: string) => {
    onChangeRecipient({ label, address });
    setIsOpen(false);
  };

  return (
    <div css={{ marginTop: spacing.s }}>
      <div css={{ position: 'relative' }} ref={wrapperRef}>
        <Input
          value={address}
          placeholder="Recipient"
          onFocus={() => {
            setIsOpen(true);
          }}
        />
        {isOpen && (
          <Card css={styles.ddlContainer}>
            {recipients &&
              recipients.map(({ address, label }) => {
                return (
                  <div
                    css={{
                      padding: spacing.xxxs,
                      width: '100%',
                      cursor: 'pointer',
                      display: 'grid',
                      gap: spacing.xxxs,
                      ':hover': { backgroundColor: `${palette.black}1a` },
                    }}
                    onClick={() => {
                      onSetContact(label, address);
                    }}
                    key={`${label}${address}`}
                  >
                    <Typography variant="bodyMedium">{label}</Typography>
                    <Typography variant="bodySmallest">{address}</Typography>
                  </div>
                );
              })}
            {canAddRecipients && (!recipients || recipients.length === 0) && (
              <div
                css={{
                  display: 'grid',
                  padding: `${spacing.xxs} ${spacing.xxxs}`,
                  textAlign: 'center',
                }}
              >
                <span>
                  <Typography variant="bodyMedium">
                    No recipient? <br /> Add a recipient to send crypto
                  </Typography>
                </span>
                <Button
                  variant="text"
                  onClick={() => navigate('/wallet/recipients/add')}
                  text="Add recipient"
                />
              </div>
            )}
            {!canAddRecipients && (
              <span
                css={{ p: { textAlign: 'center', marginTop: spacing.xxxs } }}
              >
                <Typography variant="body">
                  You do not have permission to view recipients to send
                </Typography>
              </span>
            )}
          </Card>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        <Input
          placeholder="Recipient Label"
          css={{ marginTop: spacing.xxxs }}
          value={label}
          disabled
        />
      </div>
    </div>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  ddlContainer: {
    position: 'absolute',
    left: 0,
    padding: spacing.xxxs,
    zIndex: 99,
    width: '100%',
    maxHeight: spacing.xxl,
    overflowY: 'scroll',
  },
}));
